import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { LineChart, Loader2, Eye, EyeOff } from 'lucide-react';
import { BrandLogo } from '../../components/common/BrandLogo';
import { toast } from 'sonner';
import { resetPassword } from '../../services/api/auth';
import { formatApiErrorDetail } from '../../services/api/client';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (pw.length < 6) return setError('Password minimal 6 karakter.');
    if (pw !== confirm) return setError('Konfirmasi password tidak cocok.');
    setLoading(true);
    try {
      await resetPassword(token, pw);
      toast.success('Password diperbarui', { description: 'Silakan login dengan password baru.' });
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050914] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute w-[800px] h-[800px] bg-cyan-500/10 blur-[120px] rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="w-full max-w-md bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-3xl p-8 sm:p-12 relative z-10 shadow-2xl">
        <div className="mb-8">
          <BrandLogo />
        </div>

        <h1 className="text-3xl tracking-tight font-light text-white mb-2">Buat password baru</h1>
        <p className="text-white/50 text-sm mb-8">Masukkan password baru untuk akun Anda.</p>

        {!token ? (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5" data-testid="reset-no-token">
            Tautan tidak valid. Silakan minta ulang dari halaman <Link to="/forgot-password" className="underline">Lupa password</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div>
              <label className="block text-sm text-white/70 mb-2">Password Baru</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" placeholder="••••••••"
                  className="w-full bg-[#090D1A] border border-white/10 rounded-lg px-4 py-2.5 pr-11 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-cyan-400 transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.8} /> : <Eye className="w-4 h-4" strokeWidth={1.8} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Konfirmasi Password</label>
              <input type={showPw ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" placeholder="••••••••"
                className="w-full bg-[#090D1A] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors" />
            </div>
            {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Simpan Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
